/**
 * `GET /api/auctions/[listingId]/current-bid` — auction live state for the
 * winner-checkout sheet (avoids stale SSR `winningAmount`) and to short-
 * circuit the double-charge case when the winner reopens checkout after
 * a paid order already exists for the auction.
 *
 * Consumers (3 distinct web surfaces — High tier in `API_RESPONSE_AUDIT`):
 *   - winner-checkout sheet
 *   - reserve-not-met banner
 *   - deadline-expired surface
 *
 * Field semantics:
 *   - `highestBid` / `highestBidderId` reflect the auction's live state at
 *     read time. Both may be `null` (no bids yet).
 *   - `auctionStatus` is the canonical lifecycle string from
 *     `listings.auction_status` (active / ended / settled / etc.).
 *   - `paymentDeadline` is an ISO timestamp (or null when no deadline set).
 *   - `deadlineExpired` is the SERVER's decision — clients must NOT recompute
 *     against `Date.now()` (clock skew on mobile burned us before).
 *   - `reserveNotMet` is true when a reserve price exists AND the highest
 *     bid is below it. Reserve price is not exposed (private to seller).
 *   - `existingOrderId` is only populated when the auction is NOT yet
 *     `settled` AND a paid order exists for the listing — the winner
 *     checkout sheet uses it to show "Already paid" instead of a retry UI.
 *
 * @see app/api/auctions/[listingId]/current-bid/route.ts — handler
 * @see .tondocs/payments/API_RESPONSE_AUDIT.md — multi-consumer web (High)
 */

import { z } from "zod";
import { pinned } from "./pin";

export interface AuctionCurrentBidResponse {
  highestBid: number | null;
  highestBidderId: string | null;
  auctionStatus: string | null;
  /** ISO timestamp string, or null. */
  paymentDeadline: string | null;
  deadlineExpired: boolean;
  reserveNotMet: boolean;
  existingOrderId: string | null;
}

export const AuctionCurrentBidResponseSchema = pinned<AuctionCurrentBidResponse>()(
  z.object({
    highestBid: z.number().nullable(),
    highestBidderId: z.string().nullable(),
    auctionStatus: z.string().nullable(),
    paymentDeadline: z.string().nullable(),
    deadlineExpired: z.boolean(),
    reserveNotMet: z.boolean(),
    existingOrderId: z.string().nullable(),
  }),
);
